import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    IconButton,
    Link,
    Paper,
    Snackbar,
    Slider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import adminAgentService from '../services/AdminAgentService';
import kbaseService from '../services/KbaseService';
import {
    inferVendorFromModel,
    LLM_MODELS_BY_VENDOR,
    LLM_VENDORS,
    isKnownLlmVendor,
    normalizeLlmVendor,
} from '../services/LlmCatalogService';

function normalizeString(value) {
    return String(value ?? '').trim();
}

function normalizeNumber(value) {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? n : '';
}

function normalizeTemperature(value) {
    const n = normalizeNumber(value);
    if (n === '') return '';
    if (n < 0) return 0;
    if (n > 1) return 1;
    return n;
}

function getAgentKbaseId(agent) {
    return normalizeString(
        agent?.kbaseId
        ?? agent?.kbase?.kbaseId
        ?? agent?.kbase?.id
        ?? agent?.knowledgeBaseId
        ?? agent?.knowledge_base_id
    );
}

function AgentDialog({ open, mode, initialAgent, kbases, kbasesLoading, onClose, onSubmit, submitting, t }) {
    const [agentName, setAgentName] = useState('');
    const [kbaseId, setKbaseId] = useState('');
    const [llmVendor, setLlmVendor] = useState('');
    const [llmModel, setLlmModel] = useState('');
    const [prompt, setPrompt] = useState('');
    const [temperature, setTemperature] = useState('');
    const [maxTokens, setMaxTokens] = useState('');

    const modelOptions = useMemo(() => {
        const vendor = normalizeString(llmVendor);
        return LLM_MODELS_BY_VENDOR[vendor] || [];
    }, [llmVendor]);

    const initialSignature = useMemo(() => {
        const a = initialAgent || {};
        const id = normalizeString(a?.agentId);
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        return `${id}|${normalizeString(rawVendor)}|${normalizeString(rawModel)}`;
    }, [initialAgent]);

    const initialHasVendorOrModel = useMemo(() => {
        const a = initialAgent || {};
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        return Boolean(normalizeString(rawVendor)) || Boolean(normalizeString(rawModel));
    }, [initialAgent]);

    useEffect(() => {
        if (!open) return;
        const a = initialAgent || {};
        setAgentName(normalizeString(a.agentName));
        setKbaseId(getAgentKbaseId(a));
        const rawVendor = a?.llmVendor ?? a?.vendor ?? a?.llm_vendor ?? a?.provider ?? a?.llmProvider;
        const rawModel = a?.llmModel ?? a?.model ?? a?.llm_model;
        const model = normalizeString(rawModel);
        const vendor = (() => {
            const normalized = normalizeLlmVendor(rawVendor);
            const isKnown = normalized ? isKnownLlmVendor(normalized) : false;
            if (isKnown) return normalized;
            const inferred = model ? inferVendorFromModel(model) : '';
            return inferred || normalized || '';
        })();

        setLlmVendor(normalizeString(vendor));
        setLlmModel(model);
        setPrompt(normalizeString(a.prompt));

        const temp = a.temperature;
        setTemperature(temp === 0 ? 0 : normalizeNumber(temp));

        const existingMaxTokens = a.maxTokens ?? a.maxToken;
        const max = existingMaxTokens === 0 ? 0 : normalizeNumber(existingMaxTokens);
        setMaxTokens(mode === 'create' ? (max === '' ? 1000 : max) : max);
    }, [open, initialSignature, mode, initialAgent]);

    const temperatureNumber = normalizeNumber(temperature);
    const temperatureError = temperatureNumber !== '' && (temperatureNumber < 0 || temperatureNumber > 1);
    const temperatureSliderValue = temperatureNumber === '' ? 0 : temperatureNumber;

    const maxTokensNumber = normalizeNumber(maxTokens);
    const maxTokensError = maxTokensNumber !== '' && maxTokensNumber <= 0;

    useEffect(() => {
        if (!open) return;
        if (kbasesLoading) return;
        if (mode === 'edit') return;

        const current = normalizeString(kbaseId);
        if (current) return;
        const first = (Array.isArray(kbases) ? kbases : [])[0]?.kbaseId;
        if (first) setKbaseId(String(first));
    }, [open, kbases, kbaseId, kbasesLoading, mode]);

    useEffect(() => {
        if (!open) return;

        // When editing, prefer the hydrated backend value; don't override it
        // with defaults during the initial open.
        if (mode === 'edit' && initialHasVendorOrModel) return;

        const vendor = normalizeString(llmVendor);
        const nextVendor = vendor || LLM_VENDORS[0]?.value || '';
        if (nextVendor && nextVendor !== llmVendor) {
            setLlmVendor(nextVendor);
            return;
        }

        const model = normalizeString(llmModel);
        const opts = LLM_MODELS_BY_VENDOR[nextVendor] || [];
        const firstModel = opts[0]?.value || '';
        const modelIsValid = model && opts.some((m) => m.value === model);
        if (!modelIsValid && firstModel && firstModel !== llmModel) {
            setLlmModel(firstModel);
        }
    }, [open, llmVendor, llmModel, mode, initialHasVendorOrModel]);

    const title = mode === 'edit'
        ? `${t('adminAgents.editTitle', 'Edit Agent')}: ${normalizeString(initialAgent?.agentId) || ''}`.trim()
        : t('adminAgents.newTitle', 'New Agent');

    const canSubmit = Boolean(normalizeString(agentName))
        && Boolean(normalizeString(kbaseId))
        && !temperatureError
        && !maxTokensError;

    const handleSubmit = async () => {
        const promptValue = normalizeString(prompt);
        const payload = {
            agentName: normalizeString(agentName),
            kbaseId: normalizeString(kbaseId),
            llmVendor: normalizeString(llmVendor),
            llmModel: normalizeString(llmModel),
            prompt: mode === 'edit' ? promptValue : undefined,
            temperature: (() => {
                const n = normalizeTemperature(temperature);
                return n === '' ? undefined : n;
            })(),
            maxTokens: (() => {
                const n = normalizeNumber(maxTokens);
                return n === '' ? undefined : Math.trunc(n);
            })(),
        };

        if (mode !== 'edit' && !promptValue) delete payload.prompt;

        Object.keys(payload).forEach((k) => {
            if (mode === 'edit' && k === 'prompt') return;
            if (payload[k] === '') delete payload[k];
        });

        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

        await onSubmit(payload);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 3, overflowY: 'visible' }}>
                <TextField
                    label={t('adminAgents.agentName', 'Agent Name')}
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                />
                <TextField
                    select
                    label={t('adminAgents.kbase', 'KBase')}
                    value={kbaseId}
                    onChange={(e) => setKbaseId(e.target.value)}
                    required
                    fullWidth
                    disabled={submitting}
                    helperText={kbasesLoading ? t('adminAgents.loadingKbases', 'Loading kbases…') : (kbases?.length ? '' : t('adminAgents.noKbases', 'No kbases found'))}
                >
                    {(() => {
                        const current = normalizeString(kbaseId);
                        const exists = (Array.isArray(kbases) ? kbases : []).some((k) => normalizeString(k?.kbaseId) === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{`Current: ${current}`}</MenuItem>;
                        }
                        return null;
                    })()}
                    {(Array.isArray(kbases) ? kbases : []).map((k) => (
                        <MenuItem key={String(k?.kbaseId)} value={String(k?.kbaseId || '')}>
                            {k?.kbaseName ? String(k.kbaseName) : String(k?.kbaseId || '')}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label={t('adminAgents.vendor', 'LLM Vendor')}
                    value={llmVendor}
                    onChange={(e) => {
                        const nextVendor = e.target.value;
                        setLlmVendor(nextVendor);
                        const opts = LLM_MODELS_BY_VENDOR[String(nextVendor)] || [];
                        setLlmModel(opts[0]?.value || '');
                    }}
                    fullWidth
                    disabled={submitting}
                >
                    {LLM_VENDORS.map((v) => (
                        <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label={t('adminAgents.model', 'LLM Model')}
                    value={llmModel}
                    onChange={(e) => setLlmModel(e.target.value)}
                    fullWidth
                    disabled={submitting || !normalizeString(llmVendor)}
                    helperText={!normalizeString(llmVendor) ? t('adminAgents.pickVendor', 'Select a vendor first') : ''}
                >
                    {(() => {
                        const current = normalizeString(llmModel);
                        const exists = modelOptions.some((m) => m.value === current);
                        if (current && !exists) {
                            return <MenuItem value={current}>{`Current: ${current}`}</MenuItem>;
                        }
                        return null;
                    })()}
                    {modelOptions.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label={t('adminAgents.prompt', 'Prompt')}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    fullWidth
                    disabled={submitting}
                    multiline
                    minRows={5}
                />
                <Box
                    sx={{
                        border: 1,
                        borderColor: temperatureError ? 'error.main' : 'divider',
                        borderRadius: 1,
                        px: 2,
                        py: 0.75,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color={temperatureError ? 'error.main' : 'text.secondary'}>
                            {t('adminAgents.temperature', 'Temperature (0-1)')}
                        </Typography>
                        <Slider
                            size="small"
                            value={temperatureSliderValue}
                            onChange={(event, value) => setTemperature(Array.isArray(value) ? value[0] : value)}
                            min={0}
                            max={1}
                            step={0.1}
                            valueLabelDisplay="auto"
                            disabled={submitting}
                            aria-label={t('adminAgents.temperature', 'Temperature (0-1)')}
                            sx={{ flex: 1 }}
                        />
                        <Typography variant="body2" color="text.primary">
                            {temperatureSliderValue.toFixed(1)}
                        </Typography>
                    </Box>
                    {temperatureError ? (
                        <Typography variant="caption" color="error">
                            {t('adminAgents.temperatureError', 'Temperature must be between 0 and 1')}
                        </Typography>
                    ) : null}
                </Box>
                <TextField
                    label={t('adminAgents.maxTokens', 'Max Tokens')}
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(e.target.value)}
                    fullWidth
                    disabled={submitting}
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    error={maxTokensError}
                    helperText={maxTokensError ? t('adminAgents.maxTokensError', 'Max tokens must be greater than 0') : (mode === 'create' ? t('adminAgents.maxTokensDefault', 'Default: 1000') : '')}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
                    {t('adminAgents.cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit || submitting}
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                >
                    {submitting ? t('adminAgents.saving', 'Saving…') : t('adminAgents.save', 'Save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AdminAgents({ embedded = false }) {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [agents, setAgents] = useState([]);
    const [kbases, setKbases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [kbasesLoading, setKbasesLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [dialogAgent, setDialogAgent] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const kbasesById = useMemo(() => {
        const map = new Map();
        (Array.isArray(kbases) ? kbases : []).forEach((k) => {
            const id = normalizeString(k?.kbaseId);
            if (id) map.set(id, normalizeString(k?.kbaseName) || id);
        });
        return map;
    }, [kbases]);

    const fetchAll = async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            setError('');
            setKbasesLoading(true);
            const [agentsData, kbasesData] = await Promise.all([
                adminAgentService.getAgents(clientId),
                kbaseService.getKbases(clientId),
            ]);
            setAgents(Array.isArray(agentsData) ? agentsData : []);
            setKbases(Array.isArray(kbasesData) ? kbasesData : []);
        } catch (e) {
            console.error('Error fetching admin agents', e);
            setError(t('adminAgents.fetchError', 'Failed to load admin agents'));
        } finally {
            setLoading(false);
            setKbasesLoading(false);
        }
    };

    useEffect(() => {
        if (clientId) fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const closeError = () => setError('');

    const openCreate = () => {
        setDialogMode('create');
        setDialogAgent(null);
        setDialogOpen(true);
    };

    const openEdit = (agent) => {
        const id = normalizeString(agent?.agentId);
        if (!clientId || !id) return;

        setSubmitting(true);
        (async () => {
            try {
                const data = await adminAgentService.getAgent(clientId, id);
                const full = data && typeof data === 'object' && data.agent && typeof data.agent === 'object'
                    ? data.agent
                    : data;
                setDialogMode('edit');
                setDialogAgent(full || agent || null);
                setDialogOpen(true);
            } catch (e) {
                console.error('Failed to load admin agent details', e);
                setSnackbar({ open: true, message: t('adminAgents.loadError', 'Failed to load agent'), severity: 'error' });
                setDialogMode('edit');
                setDialogAgent(agent || null);
                setDialogOpen(true);
            } finally {
                setSubmitting(false);
            }
        })();
    };

    const closeDialog = () => {
        if (submitting) return;
        setDialogOpen(false);
        setDialogAgent(null);
    };

    const submitDialog = async (payload) => {
        if (!clientId) return;
        try {
            setSubmitting(true);
            if (dialogMode === 'edit') {
                const id = normalizeString(dialogAgent?.agentId);
                if (!id) throw new Error('agentId is required');
                await adminAgentService.updateAgent(clientId, id, payload);
                setSnackbar({ open: true, message: t('adminAgents.saved', 'Agent saved'), severity: 'success' });
            } else {
                await adminAgentService.createAgent(clientId, payload);
                setSnackbar({ open: true, message: t('adminAgents.created', 'Agent created'), severity: 'success' });
            }
            closeDialog();
            fetchAll();
        } catch (e) {
            console.error('Error saving admin agent', e);
            setSnackbar({ open: true, message: t('adminAgents.saveError', 'Failed to save agent'), severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const openDelete = (agent) => {
        setDeleteTarget(agent || null);
        setDeleteDialogOpen(true);
    };

    const closeDelete = () => {
        if (deleting) return;
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    const confirmDelete = async () => {
        const agentId = normalizeString(deleteTarget?.agentId);
        if (!agentId) return;

        try {
            setDeleting(true);
            await adminAgentService.deleteAgent(clientId, agentId);
            setSnackbar({ open: true, message: t('adminAgents.deleted', 'Agent deleted'), severity: 'success' });
            closeDelete();
            fetchAll();
        } catch (e) {
            console.error('Error deleting admin agent', e);
            setSnackbar({ open: true, message: t('adminAgents.deleteError', 'Failed to delete agent'), severity: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ p: embedded ? 0 : 2 }}>
            {!embedded ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
                            <Link component={RouterLink} underline="hover" color="inherit" to="/customer-list" sx={{ fontSize: 13 }}>
                                {t('adminAgents.clientList', 'Client List')}
                            </Link>
                            <Typography color="text.primary" sx={{ fontSize: 13 }}>
                                {t('adminAgents.breadcrumbAgents', 'Bot Agents')}
                            </Typography>
                        </Breadcrumbs>
                        <Typography variant="h5" fontWeight="bold">
                            {t('adminAgents.title', 'Admin Bot Agents')}{clientId ? `: ${clientId}` : ''}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate('/customer-list')}
                            disabled={loading}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {t('adminAgents.backToClients', 'Back to Clients')}
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={openCreate}
                            disabled={loading || !clientId}
                            sx={{ textTransform: 'none', borderRadius: 3 }}
                        >
                            {t('adminAgents.new', 'New Agent')}
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={openCreate}
                        disabled={loading || !clientId}
                        sx={{ textTransform: 'none', borderRadius: 3 }}
                    >
                        {t('adminAgents.new', 'New Agent')}
                    </Button>
                </Box>
            )}

            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminAgents.agentId', 'Agent ID')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminAgents.agentName', 'Name')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminAgents.kbase', 'KBase')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminAgents.vendor', 'LLM Vendor')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminAgents.model', 'LLM Model')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('adminAgents.actions', 'Actions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {agents.map((a) => {
                            const agentId = normalizeString(a?.agentId);
                            const kbaseId = getAgentKbaseId(a);
                            const kbaseLabel = kbasesById.get(kbaseId)
                                || normalizeString(a?.kbaseName)
                                || normalizeString(a?.kbase?.kbaseName)
                                || kbaseId
                                || '-';

                            return (
                                <TableRow key={agentId || Math.random()} hover>
                                    <TableCell>{agentId || '-'}</TableCell>
                                    <TableCell>{a?.agentName || '-'}</TableCell>
                                    <TableCell>{kbaseLabel}</TableCell>
                                    <TableCell>{a?.llmVendor || '-'}</TableCell>
                                    <TableCell>{a?.llmModel || '-'}</TableCell>
                                    <TableCell>
                                        <Tooltip title={t('adminAgents.edit', 'Edit')} placement="top" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => openEdit(a)}
                                                disabled={!agentId}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('adminAgents.delete', 'Delete')} placement="top" arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => openDelete(a)}
                                                disabled={!agentId}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {agents.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    {t('adminAgents.empty', 'No agents found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={deleteDialogOpen} onClose={closeDelete} maxWidth="xs" fullWidth>
                <DialogTitle>{t('adminAgents.deleteTitle', 'Delete Agent')}</DialogTitle>
                <DialogContent>
                    {t('adminAgents.deleteMessage', 'Are you sure you want to delete this agent?')}
                    {normalizeString(deleteTarget?.agentId) ? ` (${normalizeString(deleteTarget?.agentId)})` : ''}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDelete} disabled={deleting} sx={{ textTransform: 'none' }}>
                        {t('adminAgents.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        sx={{ textTransform: 'none' }}
                    >
                        {deleting ? t('adminAgents.deleting', 'Deleting…') : t('adminAgents.confirm', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <AgentDialog
                open={dialogOpen}
                mode={dialogMode}
                initialAgent={dialogAgent}
                kbases={kbases}
                kbasesLoading={kbasesLoading}
                onClose={closeDialog}
                onSubmit={submitDialog}
                submitting={submitting}
                t={t}
            />

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={closeError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={closeError} severity="error">
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AdminAgents;
